import React, { useState } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useAnalytics, useAuth, useFirestore } from "reactfire";
import { useNavigate } from "react-router-dom";
import { useFeedback } from "../../../providers/feedback/feedback";
import LoginLayout from "../../../layouts/login_layout/login_layout";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { logEvent } from "firebase/analytics";
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAllowedModules, getDefaultAdminPath } from "../../../utils/admin_access";
import { getFirebaseAuthErrorMessage } from "../../../utils/auth_error_messages";

const TextField = styled(MuiTextField)(({ theme }) => {
  return {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "transparent", // Border color when focused
      },
      "&:hover fieldset": {
        borderColor: "transparent", // Border color on hover
      },
      "& fieldset": {
        borderColor: "transparent", // Default border color
      },
      backgroundColor: "#919eab33",
    },
  };
});

function Login() {
  const [userEmail, setUserEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setErrorMsg] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const auth = useAuth();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  async function _signInWithUSernameAndPassword() {
    const regEx =
      /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
    if (!regEx.test(userEmail)) {
      setErrorMsg({ ...error, email: "Please enter valid email" });
    }
    // else if (password.length < 6) {
    //   setErrorMsg({
    //     ...error,
    //     password: "Password should be at least 6 characters",
    //   });
    // }
    else {
      setIsLoading(true);
      try {
        const userCredential = await signInWithEmailAndPassword(auth, userEmail, password);
        logEvent(analytics, "signin", { uid: userCredential?.user.uid });

        const adminQuery = query(
          collection(firestore, "admins"),
          where("uid", "==", userCredential.user.uid),
        );
        const adminSnapshot = await getDocs(adminQuery);

        if (adminSnapshot.empty) {
          await signOut(auth);
          showSnackbar("User not found");
          setIsLoading(false);
          return;
        }

        const adminDoc = adminSnapshot.docs[0];
        const adminProfile = adminDoc.data();
        const allowedModules = getAllowedModules(adminProfile);
        const defaultPath = getDefaultAdminPath(allowedModules);

        if (defaultPath === "/admin/login") {
          await signOut(auth);
          showSnackbar("Admin access is not configured for this user");
          return;
        }

        const loginAtClient = new Date().toISOString();

        try {
          await addDoc(collection(firestore, "admin_login_audit"), {
            uid: userCredential.user.uid,
            email_id: adminProfile?.email_id || userCredential.user.email || userEmail,
            name: adminProfile?.name || "",
            role: adminProfile?.role || "",
            role_id: adminProfile?.role_id || "",
            modules: adminProfile?.modules || [],
            loginAt: serverTimestamp(),
            loginAtClient,
          });

          await updateDoc(adminDoc.ref, {
            updatedAt: loginAtClient,
            lastLoginAt: serverTimestamp(),
            lastLoginAtClient: loginAtClient,
          });
        } catch (auditError) {
          console.error("Failed to write admin login audit", auditError);
        }

        showSnackbar("Login Successfully");
        window.location.assign(defaultPath);
        return;
      } catch (error: any) {
        console.error("Failed to sign in to admin portal", error);
        const message = getFirebaseAuthErrorMessage(
          error,
          "Failed to sign in. Please try again.",
        );
        showSnackbar(message);
      } finally {
        setIsLoading(false);
      }
    }
  }

  return (
    <LoginLayout>
      <Grid container justifyContent={"center"}>
        <Grid item lg={8} md={10} sm={12} mb={3} width="100%">
          <Box mt={8}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              textAlign={"center"}
            >
              To access the admin portal, please enter your email and password
              below
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography mt={2} textAlign={"start"}>
              Email address
            </Typography>
            <TextField
              placeholder="Enter your email address"
              fullWidth
              value={userEmail}
              sx={{ mt: 1 }}
              onChange={(e) => {
                setUserEmail(e.target.value);
                setErrorMsg({ ...error, email: "" });
              }}
              helperText={error.email}
              FormHelperTextProps={{ sx: { color: "red" } }}
            />
            <Typography mt={2} textAlign={"start"}>
              Password
            </Typography>
            <TextField
              placeholder="Enter  password"
              fullWidth
              value={password}
              sx={{ mt: 1 }}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrorMsg({ ...error, password: "" });
              }}
              type={showPassword ? "text" : "password"}
              helperText={error.password}
              FormHelperTextProps={{ sx: { color: "red" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box mt={3}>
            {isLoading ? (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent={"center"}
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Button
                disabled={userEmail && password ? false : true}
                variant="contained"
                fullWidth
                size="large"
                onClick={_signInWithUSernameAndPassword}
              >
                Login
              </Button>
            )}
          </Box>
          <Grid item lg={12} md={12} sm={12}>
            <Grid container mt={1}>
              <Grid item lg={12} md={12} sm={12} textAlign={"end"}>
                <Button
                  variant="text"
                  sx={{ m: 0, p: 0 }}
                  onClick={() => navigate("/forgotPassword")}
                >
                  Forgetten Password?
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </LoginLayout>
  );
}

export default Login;
